/* eslint-disable no-underscore-dangle */
import { useAuth0 } from "@auth0/auth0-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { AuthService, User, AuthUserType } from "../../shared/types"
import { decode } from "../../shared/hooks/useAuthDecode"
import { mapAuthUser, mapAuthUserType } from "../../shared/mapAuthUsers"
import { config } from "../../shared/config"

export const useAuth0Service = (): AuthService => {
  const [user, setUser] = useState<(User & { exp: number; token: string; ownerId?: string | null }) | undefined>()

  const { isAuthenticated, isLoading, loginWithRedirect, logout, getAccessTokenSilently, getIdTokenClaims } = useAuth0()

  const isEmbedded = useMemo(() => config().embedded, [])

  useEffect(() => {
    if (isLoading || !isAuthenticated) return
    ;(async () => {
      const claims = await getIdTokenClaims()
      const token = await getAccessTokenSilently()

      const payload = token.split(`.`)[1]
      const cleaned = payload.replace(/-/g, `+`).replace(/_/g, `/`)
      const parsed = JSON.parse(window.atob(cleaned))
      const { exp } = parsed

      setUser(claims !== undefined ? { ...decode(claims.__raw), exp, token } : undefined)
    })()
  }, [isAuthenticated, isLoading])

  const authUserType = useMemo<AuthUserType>(() => mapAuthUserType(user), [user])

  const resetPassword = useCallback(async (email: string) => {
    const domain = window.location.href.includes(`staging`)
      ? `flock-platform-staging.eu.auth0.com`
      : `auth.flockcover.com`

    const clientId = window.location.href.includes(`staging`)
      ? `AMhIT9DVKy9tVBWfdbFePKdv0dAe5bb5`
      : `M4opxpWyp6m13hDQLXKNfZ26w2lgBbl1`

    await fetch(`https://${domain}/dbconnections/change_password`, {
      method: `POST`,
      headers: { "Content-Type": `application/json` },
      credentials: `omit`,
      body: JSON.stringify({
        client_id: clientId,
        connection: `Users`,
        email,
      }),
    })
  }, [])

  return useMemo<AuthService>(
    () => ({
      resetPassword,
      login: async () => {
        await loginWithRedirect({
          appState: { returnTo: `${window.location.pathname}${window.location.search}` },
        })

        return true
      },
      getToken: () => getAccessTokenSilently(),
      logout: async () =>
        logout({
          logoutParams: {
            returnTo: `${window.location.origin}/logout`,
          },
        }),
      isAuthenticated: isAuthenticated && isLoading === false && user !== undefined,
      isLoading: isLoading || (isAuthenticated && !isLoading && user === undefined),
      isEmbedded,
      getAuthId: () => user?.id ?? null,
      getAuthType: () => authUserType ?? null,
      getAuthUser: () => mapAuthUser(user),
    }),
    [
      isEmbedded,
      isAuthenticated,
      isLoading,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
      user,
      authUserType,
      resetPassword,
    ]
  )
}
